<template>
    <div>
        <DotsLoading v-if="isLoading" />
        <template v-else>
            <RouteHeader
                v-model="selectedDate"
                :route="route"
                :notices="notices"
                :general-notices="generalNotices"
                :is-printing-available="departures.length > 0"
                :invalid-date="query.fd"
            >
                <template #content>
                    <div :class="serviceClass">
                        <VirtualStation
                            class="route-header"
                            :station="selectedStopNextDepartures"
                            :route="route"
                            :has-notices="notices.length !== 0"
                            :is-today="isCurrentDay"
                            :ntr-enabled="ntrEnabled"
                            @refresh="startNextDepartureLoop"
                        >
                            <template #stop-content>
                                <div class="direction-wrapper">
                                    <h3 class="direction-title">Vers {{ direction.description }}</h3>
                                    <div class="stop-infos">
                                        <span class="visuallyhidden">{{ t('stop') }} </span>
                                        <p class="stop-name">{{ stop.noArret }} - {{ stop.nom }}</p>
                                        <small class="stop-description">{{ stop.description }}</small>
                                    </div>
                                </div>
                            </template>
                        </VirtualStation>
                    </div>
                </template>
            </RouteHeader>
            <div v-if="!hours || hours.length === 0" class="trip-container no-departures-message">
                <div v-if="!hours">
                    <p>
                        {{ t('error_occured') }}
                    </p>
                    <p>{{ t('try_again_later') }}</p>
                </div>
                <div v-else>
                    <p>{{ t('no_departures_date') }}</p>
                    <p>{{ t('choose_another_date') }}</p>
                </div>
            </div>
            <div v-else class="trip-page-wrapper">
                <div class="trip-container">
                    <div class="stop-schedule">
                        <h3 class="stop-schedule-title">{{ t('planned_schedule_stop') }}</h3>
                        <div class="next-departure">
                            <div>
                                <p class="next-departure-text">
                                    {{ t('first_next_departure', isToday(selectedDate)) }}
                                </p>
                                <p v-if="nextDeparture" class="next-departure-hour">
                                    {{ format(nextDeparture.depart, locale === 'fr' ? "H'h'mm" : 'H:mm') }}
                                </p>
                            </div>
                            <button
                                type="button"
                                class="show-schedule-button button is-outlined"
                                @click="isDeparturesTableOpen = !isDeparturesTableOpen"
                            >
                                {{ t('consult_whole_schedule') }}
                            </button>
                        </div>
                        <StopSchedule
                            :class="{ 'is-open': isDeparturesTableOpen }"
                            :departures="departures"
                            :next-departure="nextDeparture"
                            :other-headsigns="otherHeadsigns"
                            @selectHour="selectHour"
                        />
                        <ScheduleLegend class="show-for-medium" :other-headsigns="otherHeadsigns" />
                    </div>
                    <div v-if="idTrip && idTrip !== 0" class="trip-stop-list">
                        <h3 class="trip-title">{{ t('detailed_planned_trip') }}</h3>
                        <RouteMap
                            :route="route"
                            :direction="direction"
                            :selected-date="selectedDate"
                            class="map-container"
                            :stop="stop"
                            :stops="stops"
                            :default-zoom="13"
                            :hours="hours"
                        />
                        <TripStops
                            :route="route"
                            :direction="direction.description"
                            :hours="hours"
                            :no-stop="stop.noArret"
                            :selected-date="selectedDate"
                            :transfers="routeTransfers"
                            :service-class="serviceClass"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { onMounted, computed, ref, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';

import { format, addDays, isBefore, isAfter, parseISO, setHours, setMinutes } from '@@/js/utils/dateFns';
import { constants } from '@@/js/utils/constants';
import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import { useSchedulesStore } from '@@/js/stores/schedules';
import { useBreadcrumb } from '@/schedules/composables/useBreadcrumb';
import directionSlug from '@@/js/utils/directionSlug';
import DotsLoading from '@/shared/DotsLoading';
import RouteHeader from '@/schedules/components/RouteHeader';
import RouteMap from '@/schedules/components/RouteMap';
import ScheduleLegend from '@/schedules/components/ScheduleLegend';
import StopSchedule from '@/schedules/components/StopSchedule';
import TripStops from '@/schedules/components/TripStops';
import VirtualStation from '@/schedules/components/VirtualStation';

const { t, locale } = useI18n({
    messages: {
        fr: {
            all_routes_title: 'Tous les parcours',
            search_another_route: 'Autres parcours et arrêts',
            to_direction: 'Vers {direction}',
            stop: 'Arrêt',
            no_departures_date: "Il n'y a aucun passage pour cette date.",
            choose_another_date: 'Veuillez choisir une autre date.',
            planned_schedule_stop: "Horaire planifié à l'arrêt",
            first_next_departure: 'Prochain passage | Premier passage',
            consult_whole_schedule: "Voir l'horaire complet",
            detailed_planned_trip: 'Détail planifié du trajet',
            variation_warning:
                "L'heure de passage de l'autobus peut varier (circulation, conditions météorologiques, travaux routiers, etc.). Le RTC met tous ses efforts pour respecter l'horaire annoncé.",
            stop_route_to: 'Arrêt {stop} - Parcours {route} vers {direction}',
            error_occured: "Une erreur s'est produite et nous sommes désolés de cet inconvénient.",
            try_again_later: 'Veuillez réessayer plus tard.',
            no_departures_date: "Il n'y a aucun passage pour cette date.",
            choose_another_date: 'Veuillez choisir une autre date.',
        },
        en: {
            all_routes_title: 'All routes',
            search_another_route: 'Other routes and stops',
            to_direction: 'To {direction}',
            stop: 'Stop',
            no_departures_date: 'There is no departures for this date.',
            choose_another_date: 'Please choose another date.',
            planned_schedule_stop: 'Planned schedule at stop',
            first_next_departure: 'Next departure | First departure',
            consult_whole_schedule: 'Consult whole schedule',
            detailed_planned_trip: 'Detailed trip',
            variation_warning:
                'Exact bus stop times may vary due to traffic, weather, road work, etc.). RTC does everything it can to stay on schedule.',
            stop_route_to: 'Stop {stop} - Route {route} to {direction}',
            error_occured: 'An error has occured and we apologize for any inconvenience.',
            try_again_later: 'Please try again later.',
            no_departures_date: 'There is no departures for this date.',
            choose_another_date: 'Please choose another date.',
        },
    },
});

const isLoading = ref(true);
const stop = ref({});
const route = ref({});
const direction = ref();

useHead({
    title: () => isLoading.value ? `${t('all_routes_title')} | RTC` : `${t('stop_route_to', { stop: stop.value.noArret, route: route.value.noParcours, direction: direction.value.description })} | RTC`,
});

const vRoute = useRoute();
const { params, name, query } = vRoute;
const router = useRouter();

const schedulesStore = useSchedulesStore();
const {
    fetchStop,
    fetchStops,
    fetchRoute,
    getDirectionDetails,
    fetchDepartures,
    fetchTripDepartures,
    getRouteNotices,
    fetchRouteTransfers,
    fetchStopNextDepartures,
    getServiceType,
} = schedulesStore;
const { generalNotices } = storeToRefs(schedulesStore);
const { getEffectiveDate, isToday } = useEffectiveDate();

const stops = ref([]);
const departures = ref([]);
const nextDeparture = ref();
const hours = ref([]);
const idTrip = ref();
const routeTransfers = ref([]);
const selectedStopNextDepartures = ref([]);
const selectedDate = ref(params.date || getEffectiveDate(new Date(), 'yyyy-MM-dd'));
const isDeparturesTableOpen = ref(false);
const isCurrentDay = computed(() => isToday(selectedDate.value));

watch(selectedDate, (newDate) => {
    router.replace({ name, params: { ...params, date: newDate } });
});

const nextDeparturesLoop = ref(null);
const timeout = ref(null);
const pageRefreshLoop = ref(null);
const ntrEnabled = computed(() => nextDeparturesLoop.value !== null ? !!nextDeparturesLoop.value : nextDeparturesLoop.value);

function stopLoop() {
    stopNextDepartureLoop();
    timeout.value && clearTimeout(timeout.value);
}

function stopPageRefreshLoop() {
    pageRefreshLoop.value && clearInterval(pageRefreshLoop.value);
}

function stopNextDepartureLoop() {
    nextDeparturesLoop.value && clearInterval(nextDeparturesLoop.value);
    nextDeparturesLoop.value = false;
}

function startPageRefreshLoop() {
    stopPageRefreshLoop();
    pageRefreshLoop.value = setInterval(validateDay, 60 * 1000);
}

function validateDay() {
    const nextDay = addDays(parseISO(selectedDate.value), 1);
    const refreshDate = setMinutes(setHours(nextDay, 4), 15);
    if (isAfter(new Date(), refreshDate)) {
        window.location.reload();
    }
}

function startNextDepartureLoop() {
    if (nextDeparturesLoop.value != null) {
        updateNextDepartures();
    }
    const clearDelay = 2 * 60 * 60 * 1000; // 2 heures
    const refreshRate = 30 * 1000; // 30 secondes
    stopNextDepartureLoop();
    timeout.value = setTimeout(stopLoop, clearDelay);
    nextDeparturesLoop.value = setInterval(updateNextDepartures, refreshRate);
}

async function updateNextDepartures() {
    if (route.value && stop.value && direction.value) {
        const station = await fetchStopNextDepartures(
            route.value.noParcours,
            stop.value.noArret,
            direction.value.code,
            selectedDate.value,
        );
        if (station) {
            selectedStopNextDepartures.value = station;
        }
    }
}

const service = computed(() => getServiceType(route.value.codeTypeService));
const serviceClass = computed(() => service.value.class || '');
// Avis du parcours et de la direction, comme sur la page du parcours : chargés une seule fois
// par le store, sans bloquer la page, ils s'affichent dès qu'ils arrivent
const notices = computed(() => (direction.value ? getRouteNotices(params.route, direction.value.code) : []));

const mostPopularHeadsign = computed(() => {
    const counts = {};
    let compare = 0;
    let mostFrequent = null;
    departures.value.forEach((departure) => {
        const headsign = departure.lieuFinDesc;
        if (!counts[headsign]) {
            counts[headsign] = 1;
        } else {
            counts[headsign]++;
        }
        if (counts[headsign] > compare) {
            compare = counts[headsign];
            mostFrequent = departure.lieuFinDesc;
        }
    });
    return mostFrequent;
});

const otherHeadsigns = computed(() => {
    const otherHeadsigns = [];
    const colors = ['#00b4ff', '#db1e25', '#3adb76', '#ebac0e', '#d8007d'];
    departures.value.forEach((departure) => {
        if (departure.lieuFinDesc !== mostPopularHeadsign.value) {
            const departureIndex = otherHeadsigns.length;
            if (otherHeadsigns.find((other) => other.lieuFinDesc === departure.lieuFinDesc) == null) {
                otherHeadsigns.push({ lieuFinDesc: departure.lieuFinDesc, color: colors[departureIndex] });
            }
        }
    });
    return otherHeadsigns;
});

async function selectHour(idTrip) {
    isDeparturesTableOpen.value = false;
    hours.value = await fetchTripDepartures(idTrip, selectedDate.value);
}

const { addBreadcrumbLinks } = useBreadcrumb();

onMounted(async () => {
    try {
        const range = constants.CALENDAR_RANGE;
        const today = getEffectiveDate(new Date(), 'yyyy-MM-dd');
        const lastAvailableDate = getEffectiveDate(addDays(new Date(), range), 'yyyy-MM-dd');
        const date = params.date || today;
        const noStop = params.stop.split('-')[0];

        stop.value = await fetchStop(noStop);

        if (!stop.value) {
            router.replace({
                name: 'route-single',
                params: { route: params.route, direction: params.direction, date: params.date },
            });
        }

        if (isBefore(date, today)) {
            router.replace({ name, params: { ...params, date: today } });
        }
        if (isAfter(date, lastAvailableDate)) {
            router.replace({ name, params: { ...params, date: today }, query: { fd: date } });
        }

        route.value = await fetchRoute(params.route, date);
        if (!route.value) {
            route.value = await fetchRoute(params.route, date);
        }
        if (!route.value) {
            router.replace({ name: 'route-list' });
        }

        direction.value = getDirectionDetails(route.value, params.direction);

        stops.value = (await fetchStops(params.route, direction.value.code, date)) || [];

        departures.value = (await fetchDepartures(params.route, noStop, direction.value.code, date)) || [];
        if (departures.value.length > 0) {
            const now = new Date();
            nextDeparture.value = departures.value.reduce((nearest, current) => {
                const currentTime = new Date(current.depart);
                const nearestTime = new Date(nearest.depart);
                return currentTime > now && (nearestTime <= now || currentTime < nearestTime) ? current : nearest;
            }, departures.value[0]);
            idTrip.value = nextDeparture.value ? nextDeparture.value.idVoyage : null;
            hours.value = idTrip.value ? await fetchTripDepartures(idTrip.value, date) : departures.value;
        }

        routeTransfers.value = await fetchRouteTransfers(noStop, route.value.noParcours, direction.value.code, date);

        selectedStopNextDepartures.value = await fetchStopNextDepartures(
            route.value.noParcours,
            noStop,
            direction.value.code,
            date,
        );

        const pageTitle = document.querySelector('h1');
        pageTitle.innerText = `${stop.value.noArret} - ${stop.value.nom}`;
        const subtitle = `<p class="page-header-subtitle">${service.value.name} ${route.value.noParcours} - ${t('to_direction', { direction: direction.value.description })}</p>`;
        pageTitle.insertAdjacentHTML('afterend', subtitle);

        // Liens à ajouter au fil d'Ariane
        const links = [
            {
                'text': `${service.value.name} ${route.value.noParcours}`,
                'url': locale.value === 'fr'
                    ? `/horaires-et-trajets/horaires-des-bus/tous-les-parcours/${route.value.noParcours}`
                    : `/en/schedules-and-routes/bus-schedule/all-routes/${route.value.noParcours}`,
            },
            {
                'text': direction.value.description,
                'url': locale.value === 'fr'
                    ? `/horaires-et-trajets/horaires-des-bus/tous-les-parcours/${route.value.noParcours}/${directionSlug(direction.value.code, direction.value.description)}`
                    : `/en/schedules-and-routes/bus-schedule/all-routes/${route.value.noParcours}/${directionSlug(direction.value.code, direction.value.description)}`,
            },
            {
                'text': `${stop.value.noArret} - ${stop.value.nom}`,
                'url': 'current-page',
            },
        ];
        addBreadcrumbLinks(links);
    } catch {
        hours.value = null;
    } finally {
        isLoading.value = false;
    }

    startPageRefreshLoop();
    if (isCurrentDay.value) {
        startNextDepartureLoop();
    }
});

onUnmounted(() => {
    stopLoop();
    stopPageRefreshLoop();

    const existingSubtitle = document.querySelector('.page-header-subtitle');
    if (existingSubtitle) {
        existingSubtitle.remove();
    }
});
</script>

<style lang="scss" scoped>
.back-link {
    @include rem-typo(14px);
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    svg {
        transform: rotate(180deg);
    }
    @include breakpoint(medium) {
        @include rem-typo(16px);
    }
}
.route-header {
    margin-bottom: 16px;
}
.stop-infos {
    margin-top: 4px;
}
.stop-name {
    @include rem-typo(16px);
    margin: 0;
    line-height: 0.8;
    @include breakpoint(medium) {
        @include rem-typo(18px);
    }
}
.stop-description {
    @include rem-typo(12px);
    display: block;
    margin-top: 5px;
}
.end-bar {
    font-weight: 700;
}
.trip-page-wrapper {
    background-color: $color-black-0;
    border-radius: 0 0 20px 20px;
}
.trip-container {
    margin: 0 -16px;
    background-color: $color-black-0;
    border-radius: 0 0 20px 20px;
    @include breakpoint(large) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin: 0;
        background-color: $color-white;
        border-radius: 20px;
    }
    @include breakpoint(xlarge) {
        gap: 32px;
    }
}
.no-departures-message {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    font-weight: 300;
    border-radius: 0 0 20px 20px;
    p {
        margin: 0;
        text-align: center;
    }
    @include breakpoint(medium) {
        @include rem-typo(20px);
        height: 385px;
    }
}
.stop-schedule {
    display: flex;
    position: relative;
    flex-direction: column;
    border: 1px solid $color-black-8;
    border-radius: 15px;
    background-color: $color-white;
    margin: 0 16px 16px;
    @include breakpoint(medium) {
        border: none;
        background-color: transparent;
        border-radius: 0;
        margin: 0;
    }
}
.trip-stop-list {
    @include breakpoint(medium) {
        flex-grow: 1;
        flex-basis: 50%;
    }
}
.note-title {
    margin-right: 8px;
    font-weight: 700;
    @include breakpoint(medium) {
        margin-right: 24px;
    }
}
.foot-note {
    @include rem-typo(12px);
    margin: 8px 0 16px 0;
    @include breakpoint(medium) {
        @include rem-typo(16px);
        margin: 8px 0 32px 0;
    }
}
.trip-title {
    @include rem-typo(18px);
    margin: 0;
    padding: 8px 15px;
    @include breakpoint(medium) {
        @include rem-typo(20px);
        padding: 16px 0;
    }
}
.map-container {
    width: 100%;
    height: 450px;
    margin-top: 8px;
    @include breakpoint(medium) {
        margin-top: 0;
    }
}
.stop-schedule-title {
    @include rem-typo(18px);
    margin: 0;
    padding: 8px 16px;
    border-bottom: 1px solid $color-black-8;
    @include breakpoint(medium) {
        @include rem-typo(20px);
        padding: 16px;
        border: none;
    }
}
.next-departure {
    @include rem-typo(16px);
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    @include breakpoint(medium) {
        @include rem-typo(18px);
        border-bottom: 1px solid $color-black-8;
        padding-top: 0;
    }
    .next-departure-text {
        line-height: 24px;
        font-weight: 600;
        margin: 0;
    }
    .next-departure-hour {
        @include rem-typo(20px);
        font-weight: 800;
        margin: 0;
    }
}
.show-schedule-button {
    @include rem-typo(14px);

    @include breakpoint(medium) {
        display: none;
    }
}
</style>
